import { createContext, useContext } from 'react';

import type { WorkspaceSharing } from '@/features/workspace/workspace-access.js';

/**
 * The workspace a connector directory reads and writes against, plus whether the viewer may change
 * connections. Connectors are a workspace resource (global-workspaces phase 2.5), so every surface
 * that shows or mutates them is scoped to exactly one workspace: the chat "+" modal follows the
 * selected workspace, the manage tab targets the workspace it manages (which may differ). Both wrap
 * the shared directory in this scope so `ConnectorCard`, the credential forms and the OAuth driver
 * all resolve the same id without prop-drilling.
 */
export interface ConnectorScopeValue {
  /** The workspace whose connectors are read and mutated. `null` before the switcher resolves. */
  workspaceId: string | null;
  /** Whether the viewer may connect / disconnect. Read-only members get a presence-only view. */
  canManage: boolean;
  /**
   * Whether this workspace is the caller's personal one, as {@link WorkspaceSharing} answers it — the
   * same three-state answer the upload surfaces use, because the workspace row is a separate read that
   * lags the grid.
   *
   * A shared workspace is asked whose SharePoint it is connecting (ours or a customer's); a personal
   * one is not, since a personal connection is always in our own tenant. `'unknown'` behaves like
   * shared: one extra, informative step in a personal workspace costs a click, while skipping it in a
   * shared workspace hides the customer lane from the person who needs it. Omitted reads `'unknown'`.
   */
  sharing?: WorkspaceSharing;
}

const ConnectorScopeContext = createContext<ConnectorScopeValue | null>(null);

/** Provides the connector scope to a directory subtree. */
export const ConnectorScopeProvider = ConnectorScopeContext.Provider;

/**
 * Reads the surrounding connector scope. Throws when used outside a provider so a mis-wired surface
 * fails loudly rather than silently reading or writing the wrong (or a per-user) scope.
 */
export function useConnectorScope(): ConnectorScopeValue {
  const scope = useContext(ConnectorScopeContext);
  if (!scope) {
    throw new Error(
      'useConnectorScope must be used within a ConnectorScopeProvider',
    );
  }
  return scope;
}
